import React, { useEffect, useId, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'

import { ContentLayout } from '@app/layouts'
import { Header, ImageSlider, Loader, PermissionWrapper, Subheader } from '@app/components'
import { LntButton, LntForm } from '@app/components/base'
import { Show, Switch } from '@app/components/util'
import { CommonExternalSearchCatalogDrawer } from '@app/drawers/CommonExternalSearchCatalogDrawer'
import { ActivityFeed, CommentsTabWithSamples } from '@app/components/ActivityFeed'

import {
  useErrorRedirect,
  useExtendedNavigate,
  useJwtSession,
  useNotification,
  useToggle,
  useUserQuery
} from '@app/hooks'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { MONITORINGS_TASKS_KEY } from '@app/constants/queryKeys'
import { EXTERNAL_PRODUCTS_CATALOG_PAGE } from '@app/constants/page'
import { ACTIVITY_FEED_TABS_KEYS } from '@app/components/ActivityFeed/constants'

import {
  changeExternalProduct,
  createTaskResultsComment,
  deleteTaskResultsItemComment,
  getTaskResultsItem,
  getTaskResultsItemComments,
  patchTaskResultsItem,
  patchTaskResultsItemComment,
  patchTaskResultsItemPriceChecked
} from '@app/api/routes/monitoringTasks'

import { PERMISSION } from '@app/constants/permission'

import { useChangedFields } from './hooks'

import { FallbackContentImageSlider, MonitoringCharacteristics, PricesBlock, ProductCharacteristics } from './ui'
import {
  adaptCommentData,
  adaptProcessingResultsData,
  adaptPatchTaskResultsItemData,
  getAbnormalProductTagStatus,
  getExternalProductTagStatus,
  ableToSaveItem,
  ableToSaveComment
} from './lib'
import { DEFAULT_VALUES_COMMENT_FORM } from './constants'

import styles from './styles.module.scss'

import type { ReactNode } from 'react'
import type { FormResultsItemPriceData, ProcessingResultsPageInnerProps } from './types'
import type { TabsProps } from 'antd'

const { Case } = Switch

const hasMonitoringTotalCheck = (id?: string): boolean => id === 'TOTAL_CHECK'

export const ProcessingResultsPageInner: React.FC<ProcessingResultsPageInnerProps> = ({ id }) => {
  const formId = useId()
  const navigate = useExtendedNavigate()

  const { session } = useJwtSession()
  const notification = useNotification()

  const queryClient = useQueryClient()
  const productExternalDrawerToggle = useToggle()

  const { data: user } = useUserQuery()

  const { data: processingResultsData, isError: isErrorDataListItem } = useQuery({
    retry: false,
    queryKey: [MONITORINGS_TASKS_KEY.tasksItemResults, id],
    queryFn: async () => await getTaskResultsItem(id),
    select: taskResultsItemData => adaptProcessingResultsData(taskResultsItemData)
  })

  const { data: commentData } = useQuery({
    queryKey: [MONITORINGS_TASKS_KEY.tasksItemResultsComments],
    queryFn: async () => await getTaskResultsItemComments(id),
    select: taskResultsItemCommentsData => adaptCommentData(taskResultsItemCommentsData)
  })

  const invalidateResultsData = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItemResults, id] })
    await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItemResultsComments] })
  }

  const handleSaveSuccess = async (): Promise<void> => {
    await invalidateResultsData()

    notification.success({
      message: 'Успешно сохранено'
    })
  }

  const updateTaskResultsItemMutation = useMutation({
    mutationFn: patchTaskResultsItem,
    onSuccess: handleSaveSuccess
  })

  const createTaskResultsCommentMutation = useMutation({
    mutationFn: createTaskResultsComment,
    onSuccess: handleSaveSuccess
  })

  const setTaskResultsItemCheckedMutation = useMutation({
    mutationFn: patchTaskResultsItemPriceChecked,
    onSuccess: async () => {
      await invalidateResultsData()

      notification.success({
        message: 'Успешно отмечено верным'
      })
    }
  })

  const isLoadingPatchItem =
    updateTaskResultsItemMutation.isLoading ||
    createTaskResultsCommentMutation.isLoading ||
    setTaskResultsItemCheckedMutation.isLoading

  const deleteItemCommentMutation = useMutation({
    mutationFn: deleteTaskResultsItemComment,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItemResultsComments] })

      notification.success({
        message: 'Комментарий успешно удален.'
      })
    }
  })

  const patchItemCommentMutation = useMutation({
    mutationFn: patchTaskResultsItemComment,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItemResultsComments] })

      notification.success({
        message: 'Комментарий успешно изменен.'
      })
    }
  })

  const changeExternalProductMutation = useMutation({
    mutationFn: changeExternalProduct,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItemResults, id] })
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_TASKS_KEY.tasksItemResultsComments] })

      notification.success({
        message: 'Внешний товар успешно изменен.'
      })
    }
  })

  const form = useForm<FormResultsItemPriceData>()
  const {
    regularPrice: regularPriceWatch,
    promoPrice: promoPriceWatch,
    message: messageWatch,
    sampleIds: sampleIdsWatch
  } = form.watch()

  const { message: messageTouched, sampleIds: sampleIdsTouched } = form.formState.touchedFields

  const { isReadyToSubmitForm } = useChangedFields({
    changedMessage: !!messageTouched && !!messageWatch,
    changedSampleIds: !!sampleIdsTouched && !!sampleIdsWatch.length
  })

  useEffect(() => {
    if (processingResultsData?.pricesBlock) {
      const initialFormData = {
        regularPrice: processingResultsData.pricesBlock.regularPrice,
        promoPrice: processingResultsData.pricesBlock.promoPrice,
        message: '',
        sampleIds: []
      }

      form.reset(initialFormData)
    }
  }, [processingResultsData])

  useErrorRedirect(isErrorDataListItem)

  const handleSubmitPatchItem = async (formData: FormResultsItemPriceData): Promise<void> => {
    const data = adaptPatchTaskResultsItemData(formData)

    const couldSaveItem = ableToSaveItem(user)
    const couldSaveComment = ableToSaveComment(user)

    if (couldSaveItem) {
      await updateTaskResultsItemMutation.mutateAsync({ id, data })
    }

    if (couldSaveComment && data.comment) {
      await createTaskResultsCommentMutation.mutateAsync({ id, data: data.comment })
    }

    form.reset(oldValues => ({ ...oldValues, ...DEFAULT_VALUES_COMMENT_FORM }))
  }

  const handleDeleteItemComment = (id: string): void => {
    if (!id) return

    deleteItemCommentMutation.mutate({ id })
  }

  const handlePatchCommentItem = (id: string, message: string, sampleIds: number[]): void => {
    if (!session) return

    patchItemCommentMutation.mutate({
      id,
      message,
      sampleIds
    })
  }

  const handleChangeExternalProduct = async (selectedIds?: string[]): Promise<void> => {
    if (!selectedIds) return

    const externalProductId = selectedIds[0]

    changeExternalProductMutation.mutate({ id, externalProductId })
  }

  const handleSaveAndMarkAsChecked = (): void => {
    const formDataValues = form.getValues()
    const data = adaptPatchTaskResultsItemData(formDataValues)

    updateTaskResultsItemMutation.mutate({ id, data })
    setTaskResultsItemCheckedMutation.mutate({ resultIds: [id] })
  }

  const navigateToExternalProductCatalogPage = (): void => {
    const externalProduct = processingResultsData?.externalProduct

    if (!externalProduct) return

    navigate({
      pathname: EXTERNAL_PRODUCTS_CATALOG_PAGE.externalProductsCatalog,
      search: `?id=${externalProduct.id}`
    })
  }

  const activityTabsItems: TabsProps['items'] = [
    {
      key: ACTIVITY_FEED_TABS_KEYS.comments,
      label: 'Комментарии',
      children: (
        <CommentsTabWithSamples
          data={ commentData }
          onDeleteComment={ handleDeleteItemComment }
          onPatchComment={ handlePatchCommentItem }
        />
      )
    },
    {
      key: ACTIVITY_FEED_TABS_KEYS.commentsHistory,
      label: 'История изменений',
      disabled: true
    }
  ]

  const header = useMemo(() => {
    const isProductTotalCheck = hasMonitoringTotalCheck(processingResultsData?.monitoringTypeId)

    const externalProductStatus = getExternalProductTagStatus(
      isProductTotalCheck,
      processingResultsData?.externalProduct
    )
    const abnormalProductStatus = getAbnormalProductTagStatus(processingResultsData?.abnormalState)

    const title = processingResultsData?.title
    const statuses = [...externalProductStatus, ...abnormalProductStatus]

    return <Header controlProps={ { title, statuses } } />
  }, [processingResultsData])

  const toolbar: ReactNode = [
    <Show
      key='change-product'
      condition={ hasMonitoringTotalCheck(processingResultsData?.monitoringTypeId) }
    >
      <LntButton
        type={ BUTTON_TYPE.primary }
        size={ SIZE.small }
        onClick={ navigateToExternalProductCatalogPage }
      >
        Открыть в справочнике
      </LntButton>
      <LntButton
        type={ BUTTON_TYPE.primary }
        size={ SIZE.small }
        onClick={ productExternalDrawerToggle.setToggled }
      >
        Изменить товар
      </LntButton>
    </Show>,

    <PermissionWrapper
      key='save-price-and-checked'
      requiredPermissions={ [PERMISSION.markCorrectMonitoringResults] }
    >
      <LntButton
        type={ BUTTON_TYPE.secondary }
        size={ SIZE.small }
        disabled={ !isReadyToSubmitForm }
        onClick={ handleSaveAndMarkAsChecked }
        loading={ isLoadingPatchItem }
      >
        Сохранить и отметить верным
      </LntButton>
    </PermissionWrapper>,

    <LntButton
      key='save-price-and-comment'
      type={ BUTTON_TYPE.primary }
      size={ SIZE.small }
      form={ formId }
      disabled={ !isReadyToSubmitForm }
      htmlType='submit'
      loading={ isLoadingPatchItem }
    >
      Сохранить
    </LntButton>
  ]

  return (
    <>
      { header }

      <Subheader toolbar={ toolbar } />

      <ContentLayout>
        <div className={ styles.processingResultsPageInner }>
          <div className={ styles.processingResultsPageInner__column }>
            <div className={ styles.processingResultsPageInner__card }>
              <ImageSlider
                showEdgeButtons
                images={ processingResultsData?.images }
                height={ 500 }
                fallbackContent={ <FallbackContentImageSlider /> }
              />
              <ProductCharacteristics data={ processingResultsData?.productCharacteristics } />
            </div>
          </div>

          <div className={ styles.processingResultsPageInner__column }>
            <div className={ styles.processingResultsPageInner__card }>
              <MonitoringCharacteristics data={ processingResultsData?.monitoringCharacteristics } />
            </div>
            <LntForm<FormResultsItemPriceData>
              layout='vertical'
              id={ formId }
              formInstance={ form }
              onSubmit={ handleSubmitPatchItem }
            >
              <div className={ styles.processingResultsPageInner__card }>
                <Switch fallback={ <Loader /> }>
                  <Case condition={ !!processingResultsData?.pricesBlock }>
                    <PricesBlock
                      data={ processingResultsData?.pricesBlock }
                      disableInputs={ {
                        regularPrice: !!promoPriceWatch,
                        promoPrice: !!regularPriceWatch
                      } }
                    />
                  </Case>
                </Switch>
              </div>

              <div className={ styles.processingResultsPageInner__card }>
                <ActivityFeed
                  tabsItems={ activityTabsItems }
                  defaultActiveKey={ ACTIVITY_FEED_TABS_KEYS.comments }
                />
              </div>
            </LntForm>
          </div>
        </div>
      </ContentLayout>

      <CommonExternalSearchCatalogDrawer
        open={ productExternalDrawerToggle.value }
        title='Список внешних товаров'
        onClose={ productExternalDrawerToggle.setUntoggled }
        onAddProduct={ handleChangeExternalProduct }
        isConfirmed
      />
    </>
  )
}
